import "./extensions/three.convenience.js";
import "./extensions/three.toString.js";
import Commons from "oc-commons/Commons.js";
import DefaultStageSettings from "./DefaultStageSettings.js";
import Scenes from "./Scenes.js";
import {v3, quat} from "oc-threejs/short3";

/**
 * Die AbstractStage ist der zentrale Zugriffspunkt auf die Holodeck-3D-Umgebung und wird in oc-threejs an vielen
 * Stellen vorausgetzt. Die konkrete Implementierung von AbstractStage ist Stage, welche global als STAGE verfügbar ist.
 *
 * # Features der Stage
 *
 * ### Globaler Zugriffspunkt STAGE
 *
 * Die Stage als zentrales Objekt ist als STAGE global verfügbar (window.STAGE), denn normalerweise gibt es nur eine
 * Stage pro App (zwei Renderer gleichzeitig würden z.B. die meisten mobile Devices überfordern).
 *
 * ### THREE-Basisobjekte
 *
 * Im wesentlichen enthält die Stage je einen THREE.Renderer, THREE.Camera und THREE.Scene.
 * Diese zentralen Threejs Objekte werden in der Regel gemeinsam benötigt, um z.B. Bildschirm- und Weltkoordinaten
 * ineinander umzurechnen.
 *
 * ### Settings
 *
 * Weiterhin enthält die Stage allgemeine Settings, welche an diversen Stellen ausgelesen
 * werden können, siehe DefaultStageSettings.js. Die App sollte ihre Settings hier ebenfalls ablegen.
 *
 * ### Abstrakte Methoden
 *
 * Die Stage stellt folgende abstrakte Methoden bereit, die von abgeleiteten Klassen implementiert werden müssen:
 *
 * - **zoomTo(object)** zoomt die Kamera auf ein Objekt
 * - **dirty()** fordert einen neuen Rendervorgang an
 *
 * ### Object3D-Lifecycle
 *
 * Die Stage ergänzt Threejs um einen Objekt-Lifecycle. Die folgenden Callback-Methoden werden aufgerufen,
 * wenn sie auf Objekten definiert sind und die entsprechenden Ereignisse eintreten:
 *
 * - **onBeforeFirstRenderStage()** wird aufgerufen direkt bevor das Objekt zum allerersten Mal gerendert wird
 * - **onBeforeRenderStage()** wird aufgerufen direkt bevor das Objekt gerendert wird
 * - **onReattachedToScene()** wird aufgerufen, wenn ein Objekt erneut zur Szene hinzugefügt wird (auch indirekt)
 * - **onRemoveBecauseDependendParentMissing()** wird aufgerufen, wenn ein Objekt aufgrund eines
 *   dependsOnObjectsArray gelöscht wird. Objekte können als von anderen Objekten abhängig definiert werden,
 *   indem object.dependsOnObjectsArray=[otherObject] gesetzt wird.
 *
 * ### Pseudochilds
 *
 * Es gibt Methoden, um Objekte als PseudoChilds eines anderen Objektes hinzuzufügen. Ein
 * Pseudochild verhält sich wie ein Child eines anderen Objektes, ist aber nicht wirklich
 * Teil der Objekthierarchie des Parents, sondern liegt Top-Level in der Szene. Somit
 * zählen Pseudochilds z.B. nicht zur BoundingBox eines Objektes.
 *
 * ### raycastVisibleTags
 *
 * Oft möchte man für Objekte bestimmen, ob sie an bestimmten Aktionen wie drag, hover oder tap teilnehmen.
 * In der Regel werden dabei Raycasts in die Szene geschickt und geschaut, welche Objekte treffen. Der
 * Raycaster betrachtet nur Objekte, die visible sind. Mit dem Feld raycastVisibleTags kann an Objekten
 * festgelegt werden, ob sie für eine bestimmte Raycast-Aktionen visible sind oder nicht.
 * Siehe changeRaycastVisibilityDuring.
 *
 * # Code
 */
export default class AbstractStage {

    /** @type {*} */
    settings;

    // müssen von abgeleiteten Klassen gesetzt werden

    /** @type {THREE.WebGLRenderer} */
    renderer;

    /** @type {THREE.Camera} */
    camera;

    /** @type {THREE.Scene} */
    scene;

    /** @type {THREE.Scene} */
    secondPassScene;

    // wird bei jedem Rendern hochgezählt, um den Lifecycle eines Objektes zu ermitteln
    HEARTBEAT = 0;

    /**
     * @type {Element}
     * @private
     */
    _canvas;

    /**
     * @private
     */
    _objectsWithChangedOpacities = [];

    /**
     * @type {Map<unknown, unknown>}
     * @private
     */
    _materialOpacityCache = new Map();


    /**
     */
    constructor(settings) {
        // Convenience. Könnte bei Namenskollisionen mit umgebender Seite mittels string-replace-webpack-plugin ersetzt werden
        window['STAGE'] = this;

        // lustigerweise ist THREE nicht mehr im console-window greifbar, aber three...
        window['THREE'] = THREE;
        window['three'] = THREE;

        this.settings = Commons.defaultsDeepSafe(settings, DefaultStageSettings);
    }

    setSecondRenderPass(on=true) {
        if (on==(this.secondPassScene!=null)) {
            return;
        }
        this.renderer.autoClear = !on;
        this.secondPassScene = on ? new THREE.Scene() : null;
    }


    get canvas() {
        if (!this._canvas) {
            this._canvas = $(this.renderer.domElement);
        }
        return this._canvas;
    }

    /**
     * dirty sollte überschrieben werden und einen Render-Vorgang auslösen.
     */
    dirty() {
    }

    /**
     * @deprecated benutze dirty()
     */
    setDirty() {
        this.dirty();
    }

    /**
     * Läßt die Kamera auf ein Objekt zeigen, so dass die BoundingSphere genau ins Sichtfeld passt
     */
    zoomTo(object) {
    }

    /**
     * muss direkt vor jedem Render-Vorgang aufgerufen werden, um den Lifecycle zu ermöglichen
     */
    onBeforeRenderStage() {
        const OLD_HEARTBEAT = this.HEARTBEAT;
        this.HEARTBEAT++;

        const scene = this.scene;

        scene.traverse((object) => {
            if (object.__WAS_RENDERED_BEFORE__ && object.__HEARTBEAT__ < OLD_HEARTBEAT) {
                if (object.onReattachedToScene) {
                    object.onReattachedToScene(this);
                }
            }

            object.__HEARTBEAT__ = this.HEARTBEAT;

            if (object.__WAS_RENDERED_BEFORE__ == null) {
                object.__WAS_RENDERED_BEFORE__ = true;
                if (object.onBeforeFirstRenderStage) {
                    object.onBeforeFirstRenderStage(this);
                }
            }
        });

        // onBeforeRenderStage ist jetzt THREE.Object3D - Basisfunktionalität!
        Scenes.filter(scene, o => o.onBeforeRenderStage !== undefined).forEach(o => o.onBeforeRenderStage());
        if (this.secondPassScene) {
            Scenes.filter(this.secondPassScene, o => o.onBeforeRenderStage !== undefined).forEach(o => o.onBeforeRenderStage());
        }

        if (!PRODUCTION && this.settings.showDebugNotANumberInSceneError) {
            this.warnFishyObjects();
        }

        // entferne Objekte, wenn sie von anderen, aber fehlenden Objekten abhängen
        Scenes.filter(scene, x =>
            x.dependsOnObjectsArray
            && !x.dependsOnObjectsArray.every(y => y.__HEARTBEAT__ == this.HEARTBEAT)
        ).forEach(x => {
            if (x.onRemoveBecauseDependendParentMissing) {
                x.onRemoveBecauseDependendParentMissing();
            }
            scene.remove(x);
        });

        this._traverseOpacityBeforeRender();
    }

    /** */
    onAfterRenderStage() {
        this._traverseOpacityAfterRender();
    }

    warnFishyObjects() {
        const isNumberOk = (x) => !Number.isNaN(x) && Number.isFinite(x);
        const isVectorOk = (v) => isNumberOk(v.x) && isNumberOk(v.y) && isNumberOk(v.z);
        const isQuaternionOk = (q) => isNumberOk(q.x) && isNumberOk(q.y) && isNumberOk(q.z) && isNumberOk(q.w);
        const isMatrixOk = (q) => q.elements.every(isNumberOk);
        const isOk = (g) =>
            (g instanceof THREE.Vector3 && isVectorOk(g)) ||
            (g instanceof THREE.Quaternion && isQuaternionOk(g)) ||
            (g instanceof THREE.Matrix4 && isMatrixOk(g));

        const scene = this.scene;
        scene.traverse(object => {
            if (!isOk(object.matrix)) {
                if (!isOk(object.position)) {
                    console.error('The position of an object3d is fishy! ' + object + ' ' + object.position, object);
                } else if (!isOk(object.scale)) {
                    console.error('The scale of an object3d is fishy! ' + object + ' ' + object.scale, object);
                } else if (!isOk(object.quaternion)) {
                    console.error('The quaternion of an object3d is fishy! ' + object + ' ' + object.quaternion, object);
                } else {
                    console.error('The matrix of an object3d is fishy! ' + object + ' ' + object.matrix, object);
                }
            }
        });

        Debugs.checkParentChild3d(scene);
    }

    /**
     * @private
     */
    _traverseOpacityBeforeRender() {
        this._objectsWithChangedOpacities.length = 0;
        const cloneMaterial = (material, opacity) => {
            const moId = material.id * 1000 + Math.floor(opacity * 255);
            let clone = this._materialOpacityCache.get(moId);
            if (clone == null) {
                clone = material.clone();
                clone.opacity = opacity;
                clone.transparent = true;
                clone.needsUpdate = true;
                this._materialOpacityCache.set(moId, clone);
            }
            return clone;
        };
        const traverse = (object, opacity) => {
            if (object.visible) {
                opacity *= object.opacity == null ? 1 : object.opacity;
                if (object.isMesh && object.material && opacity < 1) {
                    this._objectsWithChangedOpacities.push({object, material: object.material});
                    if (_.isArray(object.material)) {
                        object.material = object.material.map(material => cloneMaterial(material, opacity));
                    } else {
                        object.material = cloneMaterial(object.material, opacity);
                    }
                }
                for (let child of object.children) {
                    traverse(child, opacity);
                }
            }
        }
        traverse(this.scene, 1);
    }

    /**
     * @private
     */
    _traverseOpacityAfterRender() {
        this._objectsWithChangedOpacities.forEach(({object, material}) => {
            object.material = material;
        });
        this._objectsWithChangedOpacities.length = 0;
    }

    /**
     * gibt den aktuellen Heartbeat (Anzahl der Rendering-Vorgänge) zurück
     */
    getHeartbeat() {
        return this.HEARTBEAT;
    }

    /**
     * Fügt einem Object ein DebugObject hinzu. Ein DebugObject ist ein PseudoChild,
     * hängt also nicht in der Objecthierarchie von Object, folgt ihm aber und nimmt
     * auch nicht an Raycasts Teil.
     */
    addAsDebugObject(object, debugObject) {
        const offset = debugObject.position.clone();
        debugObject.raycastVisibleTags = [];
        debugObject.dependsOnObjectsArray = [object];
        debugObject.onBeforeRenderStage = () => {
            debugObject.position.copy(object.getWorldPosition(v3()).add(offset));
            debugObject.quaternion.copy(object.getWorldQuaternion(quat()));
        }
        this.scene.add(debugObject);
    }

    /**
     * ein PseudoChild verhält sich wie ein Kindobjekt, liegt aber nicht
     * im Objekttree des Parents, sondern Top-Level in der Scene.
     * Vorteil: Ein PseudoChild wird nicht zur BoundingBox des Parents gerechnet, und
     * man kann definieren, welchen Eigenschaften des Parents es folgt
     * (position, quaternion und scale).
     */
    addAsPseudoChild(parent, pseudoChild, options) {
        const container = this.createPseudoChildContainer(parent, options);
        container.add(pseudoChild);
        //options.onFollow(parent,container,pseudoChild);
    }

    /**
     * erzeugt einen PseudoChildContainer, welcher Pseudochilds aufnehmen kann
     */
    createPseudoChildContainer(parent, options) {
        options = Commons.defaultsDeepSafe({}, options, {
            followPosition: true,
            followQuaternion: true,
            followScale: true,
            onFollow: (parent, container) => {
            },
        });
        const container = new THREE.Group();
        container.name = parent.name + '_PseudoChildContainer';
        container.parentPseudo = parent;
        container.raycastVisibleTags = [];
        container.dependsOnObjectsArray = [parent];
        parent.onReattachedToScene = () => {
            scene.add(parent.__pseudoChildContainer);
            delete (parent.__pseudoChildContainer);
        }
        container.onRemoveBecauseDependendParentMissing = () => {
            parent.__pseudoChildContainer = container;
        }
        container.onBeforeRenderStage = () => {
            if (options.followPosition) {
                container.position.copy(parent.getWorldPosition(v3()));
            }
            if (options.followQuaternion) {
                container.quaternion.copy(parent.getWorldQuaternion(quat()));
            }
            if (options.followScale) {
                container.scale.copy(parent.getWorldScale());
            }
            options.onFollow(parent, container);
        };
        scene.add(container);
        return container;
    }

    /**
     * Setzt während der Ausführung von callback vorübergehend alle Objekte mit vorhandenem raycastVisibleTags - Array sichtbar,
     * falls das raycastVisibleTags - Array das Tag enthält, und unsichtbar, falls das raycastVisibleTags - Array das Tag nicht enthält.
     * @example
     * // Fall 1: hier würde matched object enthalten
     * object.raycastVisibleTags = undefined;
     * stage.changeRaycastVisibilityDuring('drag',()=>{
     *    const matched = Screens.fetchObjectAtMouse(event, [object]);
     * });
     *
     * // Fall 2: hier würde matched object nicht enthalten
     * object.raycastVisibleTags = ['tap'];
     * stage.changeRaycastVisibilityDuring('drag',()=>{
     *    const matched = Screens.fetchObjectAtMouse(event, [object]);
     * });
     *
     * // Fall 3: hier würde matched object enthalten
     * object.raycastVisibleTags = ['drag','tap'];
     * stage.changeRaycastVisibilityDuring('drag',()=>{
     *    const matched = Screens.fetchObjectAtMouse(event, [object]);
     * });
     */
    changeRaycastVisibilityDuring(tag, callback) {

        const objects = [];

        this.scene.traverse((o) => {
            if (o.raycastVisibleTags != null) {
                objects.push([o, o.visible]);
                o.visible = o.raycastVisibleTags.includes(tag);
            }
        });

        const result = callback();

        objects.forEach(([o, oldVisible]) => {
            o.visible = oldVisible;
        });

        return result;
    }


    /**
     * convenience-getter, gibt an, wie viel Einheiten ein Meter in der Unit der Stage ist
     */
    get mInUnit() {
        return STAGE.settings.ruler.mInUnit;
    }

}
